import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface CreateDraftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItemCount: number;
  onConfirm: (description: string, notes?: string) => void;
  loading?: boolean;
}

export function CreateDraftDialog({
  open,
  onOpenChange,
  selectedItemCount,
  onConfirm,
  loading = false,
}: CreateDraftDialogProps) {
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  const handleConfirm = () => {
    if (!description.trim()) return;
    onConfirm(description.trim(), notes.trim() || undefined);
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setDescription("");
      setNotes("");
    }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Create Draft Invoice</DialogTitle>
          <DialogDescription>
            {selectedItemCount} WIP item{selectedItemCount !== 1 ? "s" : ""} selected. Provide a description to identify this draft.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="draft-description">
              Description <span className="text-destructive">*</span>
            </Label>
            <Input
              id="draft-description"
              placeholder="e.g. January 2026 - Professional Services"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && description.trim()) {
                  handleConfirm();
                }
              }}
              autoFocus
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="draft-notes">Notes</Label>
            <Textarea
              id="draft-notes"
              placeholder="Optional notes for this invoice..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!description.trim() || loading}
          >
            {loading ? "Creating..." : "Create Draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
